import { useCallback, useEffect, useRef, useState } from "react";

/**
 * The edits of one Settings section. `initial` is what the server holds now; while nothing is
 * being edited the draft follows it (a refetch after a save shows the saved values), but a refetch
 * never overwrites edits in progress. `markSaved` makes the current values the new baseline.
 */
export function useSectionDraft<T extends object>(initial: T, isEqual: (a: T, b: T) => boolean = shallowEqual) {
  const [base, setBase] = useState<T>(initial);
  const [values, setValues] = useState<T>(initial);
  const dirty = !isEqual(values, base);
  const dirtyRef = useRef(dirty);
  dirtyRef.current = dirty;

  const initialKey = JSON.stringify(initial);
  const initialRef = useRef(initial);
  initialRef.current = initial;
  useEffect(() => {
    if (dirtyRef.current) return;
    setBase(initialRef.current);
    setValues(initialRef.current);
  }, [initialKey]);

  const set = useCallback(<K extends keyof T>(field: K, value: T[K]) => {
    setValues((prev) => ({ ...prev, [field]: value }));
  }, []);

  const markSaved = useCallback((saved: T) => {
    setBase(saved);
  }, []);

  const discard = useCallback(() => setValues(base), [base]);

  return { values, base, setValues, set, dirty, markSaved, discard };
}

export function shallowEqual<T extends object>(a: T, b: T): boolean {
  const left = a as Record<string, unknown>;
  const right = b as Record<string, unknown>;
  const keys = Object.keys(left).concat(Object.keys(right));
  return keys.every((key) => left[key] === right[key]);
}

/** Reports a section's dirty flag to the page (the unsaved-changes guard), and clears it on unmount. */
export function useReportDirty(onDirtyChange: ((dirty: boolean) => void) | undefined, dirty: boolean) {
  const ref = useRef(onDirtyChange);
  ref.current = onDirtyChange;
  useEffect(() => {
    ref.current?.(dirty);
  }, [dirty]);
  useEffect(() => () => ref.current?.(false), []);
}
